


























































import Vue from 'vue';
import * as actions from '@/store/action-types';
import StatsService from '@/api/stats.service';
import { IBoostStats, IReactivationStats, LoadState, SaveType } from '@/store/types';
import { TopLevelSpec } from 'vega-lite';
import VueVegaEmbed from '@/components/Vis/Vega/VueVegaEmbed.vue';
import { genBarSpec } from '@/components/Vis/Vega/Specs/BarSpec';
import { timeDay, timeMonth } from 'd3-time';
import { LOAD_STATE, SAVE_TYPE } from '@/store/shared';
import { mapGetters } from 'vuex';
import { formatSmallMoney } from '@/helpers/jsUtils';
import { descending, min } from 'd3-array';
import { shortMonthYearLabel } from '@/helpers/date-utils';
import { SESSION, currencyFormat } from '@/helpers/stats-utils';
import BarLoader from '@/components/UI/Loader/BarLoader.vue';
import FooterLoader from '@/components/UI/Loader/FooterLoader.vue';
import HeaderLoader from '@/components/UI/Loader/HeaderLoader.vue';

interface ReactivationBreakdown {
  label: string;
  primary: string;
  sub: string;
  subColor: string;
  reactivationRate: number | null;
  reactivated: number;
  total: number;
  boostedRevenue: number;
}

export default Vue.extend({
  name: 'BoostedRevenue',
  components: {
    VueVegaEmbed,
    BarLoader,
    HeaderLoader,
    FooterLoader,
  },
  data() {
    return {
      LOAD_STATE,
      localReactivationStats: [] as IReactivationStats[],
      localReactivationStatsLoadState: LOAD_STATE.UNLOADED,
    };
  },
  async created() {
    // this.$store.dispatch(actions.FETCH_BOOSTED_REVENUE_STATS, { breakdown: ['invoiceMonth'] });
    this.$store.dispatch(actions.FETCH_BOOSTED_REVENUE_STATS);
    this.fetchReactivationStats();
  },
  computed: {
    ...mapGetters(['monthsAgoBoostedRevenue', 'monthsAgoBoostedRevenueDelta', 'lastMonthLabel']),
    loading(): boolean {
      return this.localReactivationStatsLoadState !== LOAD_STATE.LOADED;
    },
    stats(): IBoostStats[] {
      return this.$store.state.boostedRevenueStats;
    },
    statsLoadState(): LoadState {
      return this.$store.state.boostedRevenueStatsLoadState;
    },
    statLines(): ReactivationBreakdown[] {
      if (this.localReactivationStatsLoadState !== LOAD_STATE.LOADED || this.localReactivationStats.length === 0) {
        return [];
      }
      if (this.localReactivationStats) {
        const statsToShow = [
          [SAVE_TYPE.PAUSE, 'Added Revenue From Pauses'],
          [SAVE_TYPE.DISCOUNT, 'Added Revenue From Discounts'],
          [SAVE_TYPE.ABANDON, 'Added Revenue From Abandons'],
          [SAVE_TYPE.CONTACT, 'Added Revenue From Support'],
        ] as [SaveType, string][];

        const acc = [] as ReactivationBreakdown[];
        statsToShow.forEach(([saveType, label]) => {
          const d = this.localReactivationStats.find((s) => s.saveType === saveType);
          if (d) {
            let avgBoost = 0;
            if (d.reactivated) {
              avgBoost = d.boostedRevenue / d.reactivated;
            }
            acc.push({
              label,
              primary: formatSmallMoney(d.boostedRevenue),
              sub: `${d.total} ${d.total === 1 ? 'customer' : 'customers'}`,
              subColor: 'text-gray-500 text-sm',
              ...d,
            });
          }
        });
        return acc;
      }
      return [];
    },
    chartVegaSpec(): TopLevelSpec | null {
      if (this.stats && this.stats.length) {
        type dateItem = {
          'Boosted Revenue': number;
          Month: string;
          Realized: 'Actual' | 'Projected';
          'Months with Churnkey': number;
        };
        // @ts-ignore
        const currencies = this.stats.reduce((acc, s) => {
          // @ts-ignore
          return [...new Set([...acc, ...Object.keys(s.currencyBreakdown)])];
        }, [] as string[]);
        const numMonths = this.stats.length;
        const startMonth = numMonths - 12;
        const endMonth = numMonths;
        const now = new Date();
        const orgCreatedDate = min(this.stats, (d) => d.invoiceStartDate) as Date;
        const chartData = this.stats.reduce((acc, item) => {
          const monthsSince = timeMonth.count(orgCreatedDate, item.invoiceStartDate);
          const cs = currencies.reduce(
            (cacc, currency) => {
              const v = item.currencyBreakdown[currency]?.unitsDecimal || 0;
              const formattedV = currencyFormat(v, currency);
              cacc[currency] = formattedV;
              return cacc;
            },
            {} as Record<string, string>
          );

          // don't include current month if its within the first 4 days
          if (timeDay.count(item.invoiceStartDate, now) < 5) {
            return acc;
          }
          if (item.boostedRevenue) {
            acc.push({
              'Boosted Revenue': item.boostedRevenue,
              // Month: timeDay.offset(new Date(item.invoiceStartDate), 0),
              Month: shortMonthYearLabel(item.invoiceStartDate),
              Realized: 'Actual',
              'Months with Churnkey': monthsSince,
              // @ts-ignore
              ...cs,
            });
            if (item.invoiceEndDate > now) {
              const days = timeDay.count(item.invoiceStartDate, now);
              const daysProjected = timeDay.count(now, item.invoiceEndDate);
              const projectedRev = (item.boostedRevenue / days) * daysProjected;
              acc.push({
                'Boosted Revenue': projectedRev,
                // Month: timeDay.offset(new Date(item.invoiceStartDate), 0),
                Month: shortMonthYearLabel(item.invoiceStartDate),
                Realized: 'Projected',
                'Months with Churnkey': monthsSince,
                // @ts-ignore
                ...currencies.reduce(
                  (cacc, c) => {
                    cacc[c] = '-';
                    return cacc;
                  },
                  {} as Record<string, string>
                ),
              });
            }
          }
          return acc;
        }, [] as dateItem[]);
        return genBarSpec({
          data: chartData,
          xField: 'Month',
          xSortField: 'Months with Churnkey',
          yField: 'Boosted Revenue',
          colorField: 'Realized',
          xLabels: true,
          yLabels: true,
          barLabels: true,
          yFormat: '$0.2s',
          colorScale: {
            domain: ['Actual', 'Projected'],
            range: ['#3c5ccf', '#BAC5EE'],
          },
          tooltipFields: [
            { field: 'Month', title: 'Month' },
            { field: 'Boosted Revenue', title: 'Boosted Revenue', format: '$0.2s', type: 'quantitative' },
            ...currencies.map((c) => ({ field: c, title: c })),
          ],
          // xAxisTitle: 'Month',
          // yAxisTitle: 'Churn Rate',
        });
      }
      return null;
    },
  },
  methods: {
    exportBoostedRevenueData() {
      const sortedData = this.stats
        .map((d) => ({
          Month: d.invoiceMonth,
          'Boosted Revenue': d.boostedRevenue,
        }))
        .sort((a, b) => descending(a.Month, b.Month));
      this.$store.dispatch(actions.EXPORT_DATA, {
        data: sortedData,
        name: 'boosted-revenue',
        csv: true,
      });
    },
    async fetchReactivationStats() {
      try {
        this.localReactivationStatsLoadState = LOAD_STATE.LOADING;
        const stats = await StatsService.reactivationStats({ breakdown: [SESSION.SAVE_TYPE] });
        if (stats) {
          this.localReactivationStats = stats;
        }
        this.localReactivationStatsLoadState = LOAD_STATE.LOADED;
      } catch (e) {
        console.log(e);
        this.localReactivationStatsLoadState = LOAD_STATE.ERROR_LOADING;
      }
    },
  },
});
